import React, { useEffect, useState } from "react";
import {
    Row,
    Col,
    FormGroup,
    Label,
    Input,
    Container,
    FormFeedback,
    Form,
} from "reactstrap";
import { useFormik } from "formik";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import { useNavigate, useParams } from "react-router-dom";
import ResultHandler from "../../../components/ResultHandler";
import FormButtonHandler from "../../../components/FormButtonHandler";
import SearchSelectBox from "../../../components/SearchSelectBox";
import { useResponse } from "../../../hooks/useResponse";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import { getStatusDeals } from "../../../api/statusDeals";
import { getDeliverables } from "../../../api/deliverable";
import { getServices } from "../../../api/service";
import { getLeads } from "../../../api/lead";
import { editDeal, getDeal } from "../../../api/deal";
import { getFeedbacks } from "../../../api/feedback";

const EditDeal = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { id } = useParams();

    const [initialValues, setInitialValues] = useState({
        budget: "",
        StatusDeal: null,
        deliverables: [],
        services: [],
        leadId: null,
        feedback: [],
    });

    const validationSchema = Yup.object().shape({
        budget: Yup.number()
            .required(t("Budget is required"))
            .min(0, t("Budget must be at least 0")),
        StatusDeal: Yup.object().nullable().required(t("StatusDeal is required")),
        deliverables: Yup.array(),
        services: Yup.array(),
        leadId: Yup.object().nullable().required(t("Lead is required")),
        feedback: Yup.array(),
    });

    const { setData, loading, error, success } = useResponse(editDeal, () => {
        navigate("/deals");
    });

    useEffect(() => {
        const fetchDeal = async () => {
            try {
                const res = await getDeal(id);
                const data = res?.data;

                setInitialValues({
                    budget: data?.budget ?? "",
                    StatusDeal: data?.StatusDeal
                        ? { label: data.StatusDeal.name, value: data.StatusDeal._id || data.StatusDeal.id }
                        : null,
                    deliverables: data?.deliverables
                        ? data.deliverables.map((d) => ({
                            label: d.deliverable,
                            value: d._id || d.id,
                        }))
                        : [],
                    services: data?.services
                        ? data.services.map((s) => ({
                            label: s.names,
                            value: s._id || s.id,
                        }))
                        : [],
                    leadId: data?.leadId
                        ? { label: data.leadId.name, value: data.leadId._id || data.leadId.id }
                        : null,
                    feedback: data?.feedbackId
                        ? data.feedbackId.map((f) => ({
                            label: f.clientSatisfaction,
                            value: f._id || f.id,
                        }))
                        : [],
                });
            } catch (err) {
                console.error("Failed to fetch deal", err);
            }
        };

        fetchDeal();
    }, [id]);

    const formik = useFormik({
        enableReinitialize: true,
        initialValues,
        validationSchema,
        onSubmit: (values) => {
            const payload = {
                budget: Number(values.budget),
                StatusDeal: values.StatusDeal?.value || null,
                deliverables: values.deliverables?.map((d) => d.value) || [],
                services: values.services?.map((s) => s.value) || [],
                feedbackId: values.feedback?.map((f) => f.value) || [],
                leadId: values.leadId?.value || null,
                logs: [], // Assuming you want to keep empty logs here
            };
            setData({ id, data: payload, type: "edit" });
        },
    });

    return (
        <div className="page-content">
            <Container fluid>
                <Breadcrumbs title={`${t("btns.Edit")} ${t("Deal")}`} />

                <Form
                    className="needs-validation"
                    onSubmit={(e) => {
                        e.preventDefault();
                        formik.handleSubmit();
                    }}
                >
                    <Row>
                        {/* Budget Input */}
                        <Col md="6">
                            <FormGroup className="mb-3">
                                <Label>{t("Budget")}</Label>
                                <Input
                                    name="budget"
                                    placeholder={t("Budget")}
                                    type="number"
                                    min={0}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.budget}
                                    invalid={formik.touched.budget && !!formik.errors.budget}
                                />
                                <FormFeedback>{formik.errors.budget}</FormFeedback>
                            </FormGroup>
                        </Col>

                        {/* Status Deal Select */}
                        <Col md="6">
                            <FormGroup className="mb-3">
                                <Label>{t("Status Deal")}</Label>
                                <SearchSelectBox
                                    api={getStatusDeals}
                                    name="StatusDeal"
                                    placeholder={t("Select Status Deal")}
                                    value={formik.values.StatusDeal}
                                    label="name"
                                    setValue={(val) => formik.setFieldValue("StatusDeal", val)}
                                    error={formik.touched.StatusDeal && !!formik.errors.StatusDeal}
                                    isMulti={false}
                                />
                                {formik.touched.StatusDeal && formik.errors.StatusDeal && (
                                    <div className="text-danger mt-1">{formik.errors.StatusDeal}</div>
                                )}
                            </FormGroup>
                        </Col>
                        {/* Services Multi-Select */}
                        <Col md="6">
                            <FormGroup className="mb-3">
                                <Label>{t("Services")}</Label>
                                <SearchSelectBox
                                    api={getServices}
                                    name="services"
                                    placeholder={t("Select Services")}
                                    value={formik.values.services}
                                    label="names"
                                    isMulti={true}
                                    setValue={(val) => formik.setFieldValue("services", val)}
                                    error={formik.touched.services && !!formik.errors.services}
                                />
                                {formik.touched.services && formik.errors.services && (
                                    <div className="text-danger mt-1">{formik.errors.services}</div>
                                )}
                            </FormGroup>
                        </Col>

                        {/* Lead Select */}
                        <Col md="6">
                            <FormGroup className="mb-3">
                                <Label>{t("Lead")}</Label>
                                <SearchSelectBox
                                    api={getLeads}
                                    name="leadId"
                                    placeholder={t("Select Lead")}
                                    value={formik.values.leadId}
                                    label="name"
                                    setValue={(val) => formik.setFieldValue("leadId", val)}
                                    error={formik.touched.leadId && !!formik.errors.leadId}
                                />
                                {formik.touched.leadId && formik.errors.leadId && (
                                    <div className="text-danger mt-1">{formik.errors.leadId}</div>
                                )}
                            </FormGroup>
                        </Col>
                    </Row>

                    <FormButtonHandler btnTxt={t("Update")} loading={loading} />

                    <ResultHandler
                        error={error}
                        success={success}
                        successMsg={t("Updated Successfully!")}
                    />
                </Form>
            </Container>
        </div>
    );
};

export default EditDeal;
